// src/lib/message-sends-garde.js — L'ÉCRAN CESSE DE MENTIR SUR CE QU'IL A FAIT.
//
// ══ POURQUOI (décision Dimitri, 2026-09-08) ═════════════════════════════════
//
//   « Le message_sends qui confirme un enregistrement qui n'a pas eu lieu :
//     traite-le aussi. Un écran qui me ment sur ce qu'il a fait, c'est pire
//     qu'un écran qui échoue. »
//
// ══ LE DÉFAUT ═══════════════════════════════════════════════════════════════
//
// La policy `admin_update_non_sms` de `message_sends` porte
// `(current_role_safe() = 'admin') AND (canal <> 'sms')` — vérifié dans
// `pg_policies` le 2026-09-08.
//
// Un UPDATE client sur un envoi **SMS** matche donc **zéro ligne, SANS erreur**.
// PostgREST rend `{ data: [], error: null }` : le code teste `if (e) throw`,
// ne voit rien, et l'écran affiche « enregistré ».
//
// C'est le faux succès silencieux que ce dépôt connaît et documente déjà
// (ADR-207) : « UPDATE bloqué par USING RLS = 0 ligne SANS erreur → toujours
// `.select('id')` + rowcount-check ». La règle existait ; quatre appels ne
// l'appliquaient pas.
//
// ══ CE QUE CE MODULE EST, ET N'EST PAS ══════════════════════════════════════
//
// C'est un CONSTAT partagé, pas un contournement : il ne fait pas passer
// l'écriture, il empêche de prétendre qu'elle a eu lieu. Un seul texte, pour
// que les quatre écrans disent la même chose — quatre messages qui divergent
// sur le même refus, c'est quatre façons de comprendre de travers.
//
// ⚠️ LE REMÈDE COMPLET EST AILLEURS, et il est déjà écrit pour UN chemin :
// `src/tabs/messages/SmsSendCard.jsx` porte en tête « jamais
// `supabase.from('message_sends')` — uniquement `saveSmsSend`/`deleteSmsSend`,
// action serveur validée. C'est TOUT l'objet du lot ». Faire passer les trois
// autres écrans par cette action est un lot à part entière. Ici on ferme le
// mensonge, on ne refait pas le chemin.

/** Ce qu'on dit quand une écriture n'a touché aucune ligne. */
export const REFUS_SMS =
  "Cet envoi est un SMS : il ne se modifie pas depuis cet écran. " +
  "Rien n'a été enregistré. Ouvrez sa carte dédiée pour le changer.";

export const REFUS_INCONNU =
  "L'enregistrement n'a modifié aucune ligne — il a été refusé par la base. " +
  "Rien n'a été enregistré. Rechargez la page et réessayez.";

/**
 * Une écriture a-t-elle VRAIMENT touché quelque chose ?
 *
 * @param {{data?: Array|null, error?: object|null}} res — retour PostgREST,
 *        obtenu avec `.select('id')` (sans lui, `data` est null et on ne peut
 *        RIEN conclure — d'où le troisième cas ci-dessous).
 * @param {{attendus?: number, canal?: string|null}} [opts]
 * @returns {{ok: boolean, touches: number, message: string|null}}
 */
export function verifierEcriture(res, opts = {}) {
  if (res && res.error) {
    return {
      ok: false,
      touches: 0,
      message: res.error.message || String(res.error),
    };
  }
  const lignes = Array.isArray(res && res.data) ? res.data : null;

  // ⚠️ Pas de `.select()` ⇒ on ne SAIT PAS. Ne jamais conclure au succès dans
  // ce cas : c'est très exactement l'hypothèse qui a produit le défaut.
  if (lignes === null) {
    return {
      ok: false,
      touches: 0,
      message:
        "Impossible de vérifier ce qui a été enregistré (aucun retour de la base). " +
        "Rechargez la page pour voir l'état réel.",
    };
  }

  const attendus = Number.isFinite(opts.attendus) ? opts.attendus : 1;
  if (lignes.length >= attendus)
    return { ok: true, touches: lignes.length, message: null };

  // Zéro ligne : la cause la plus fréquente et la plus surprenante est le SMS.
  if (lignes.length === 0) {
    return {
      ok: false,
      touches: 0,
      message: opts.canal === "sms" ? REFUS_SMS : REFUS_INCONNU,
    };
  }

  // Écriture PARTIELLE (action en masse) : on dit le chiffre, pas « ça a marché ».
  return {
    ok: false,
    touches: lignes.length,
    message:
      `Seulement ${lignes.length} envoi(s) sur ${attendus} ont été modifiés — ` +
      "les autres ont été refusés par la base (les SMS ne se modifient pas depuis cet écran). " +
      "Rechargez la page pour voir l'état réel.",
  };
}
